import { CodeBlock, InfoBox, SectionTitle, SubsectionTitle } from './shared';

export function GatewayOverviewContent() {
  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-4xl font-bold text-white mb-4">Gateway 网关服务运行手册</h1>
        <p className="text-base text-slate-500 mb-6">最后更新：2025-12-09</p>
      </div>

      <SectionTitle id="what-is">是什么</SectionTitle>
      <ul className="space-y-3 text-slate-300 text-base">
        <li className="flex items-start gap-2">
          <i className="ri-checkbox-circle-line text-green-400 mt-1 flex-shrink-0"></i>
          <span>拥有单一 Baileys/Telegram 连接和控制/事件平面的常驻进程。</span>
        </li>
        <li className="flex items-start gap-2">
          <i className="ri-checkbox-circle-line text-green-400 mt-1 flex-shrink-0"></i>
          <span>替代旧版 <code className="px-2 py-1 bg-slate-800 rounded text-cyan-400">gateway</code> 命令。CLI 入口点：<code className="px-2 py-1 bg-slate-800 rounded text-cyan-400">openclaw gateway</code>。</span>
        </li>
        <li className="flex items-start gap-2">
          <i className="ri-checkbox-circle-line text-green-400 mt-1 flex-shrink-0"></i>
          <span>运行直到停止；出现致命错误时以非零退出码退出，以便 supervisor 重启它。</span>
        </li>
      </ul>

      <SectionTitle id="how-to-run">如何运行（本地）</SectionTitle>
      <CodeBlock
        language="bash"
        code={`openclaw gateway --port 18789
# 在 stdio 中获取完整的调试/追踪日志：
openclaw gateway --port 18789 --verbose
# 如果端口被占用，终止监听器然后启动：
openclaw gateway --force
# 开发循环（TS 更改时自动重载）：
pnpm gateway:watch`}
      />

      <div className="space-y-4 mt-6">
        <ul className="space-y-3 text-slate-300 text-base">
          <li className="flex items-start gap-2">
            <i className="ri-arrow-right-s-line text-green-400 mt-1 flex-shrink-0"></i>
            <div>
              <strong>配置热重载</strong>监视 <code className="px-2 py-1 bg-slate-800 rounded text-cyan-400">~/.openclaw/openclaw.json</code>（或 <code className="px-2 py-1 bg-slate-800 rounded text-cyan-400">OPENCLAW_CONFIG_PATH</code>）。
              <ul className="ml-4 mt-2 space-y-1 text-sm text-slate-400">
                <li>默认模式：<code className="px-2 py-1 bg-slate-800 rounded text-cyan-400">gateway.reload.mode="hybrid"</code>（热应用安全更改，关键更改时重启）。</li>
                <li>热重载在需要时通过 <strong>SIGUSR1</strong> 使用进程内重启。</li>
                <li>使用 <code className="px-2 py-1 bg-slate-800 rounded text-cyan-400">gateway.reload.mode="off"</code> 禁用。</li>
              </ul>
            </div>
          </li>
          <li className="flex items-start gap-2">
            <i className="ri-arrow-right-s-line text-green-400 mt-1 flex-shrink-0"></i>
            <span>将 WebSocket 控制平面绑定到 <code className="px-2 py-1 bg-slate-800 rounded text-cyan-400">127.0.0.1:&lt;port&gt;</code>（默认 18789）。</span>
          </li>
          <li className="flex items-start gap-2">
            <i className="ri-arrow-right-s-line text-green-400 mt-1 flex-shrink-0"></i>
            <div>
              <strong>同一端口也提供 HTTP 服务</strong>（控制界面、hooks、A2UI）。单端口多路复用。
              <ul className="ml-4 mt-2 space-y-1 text-sm text-slate-400">
                <li>OpenAI Chat Completions（HTTP）：<code className="px-2 py-1 bg-slate-800 rounded text-cyan-400">/v1/chat/completions</code></li>
                <li>OpenResponses（HTTP）：<code className="px-2 py-1 bg-slate-800 rounded text-cyan-400">/v1/responses</code></li>
                <li>Tools Invoke（HTTP）：<code className="px-2 py-1 bg-slate-800 rounded text-cyan-400">/tools/invoke</code></li>
              </ul>
            </div>
          </li>
          <li className="flex items-start gap-2">
            <i className="ri-arrow-right-s-line text-green-400 mt-1 flex-shrink-0"></i>
            <span>默认在 <code className="px-2 py-1 bg-slate-800 rounded text-cyan-400">canvasHost.port</code>（默认 <code className="px-2 py-1 bg-slate-800 rounded text-cyan-400">18793</code>）上启动 Canvas 文件服务器，从 <code className="px-2 py-1 bg-slate-800 rounded text-cyan-400">~/.openclaw/workspace/canvas</code> 提供 <code className="px-2 py-1 bg-slate-800 rounded text-cyan-400">http://&lt;gateway-host&gt;:18793/__openclaw__/canvas/</code>。使用 <code className="px-2 py-1 bg-slate-800 rounded text-cyan-400">canvasHost.enabled=false</code> 或 <code className="px-2 py-1 bg-slate-800 rounded text-cyan-400">OPENCLAW_SKIP_CANVAS_HOST=1</code> 禁用。</span>
          </li>
          <li className="flex items-start gap-2">
            <i className="ri-arrow-right-s-line text-green-400 mt-1 flex-shrink-0"></i>
            <span>输出日志到 stdout；使用 launchd/systemd 保持运行并轮转日志。</span>
          </li>
          <li className="flex items-start gap-2">
            <i className="ri-arrow-right-s-line text-green-400 mt-1 flex-shrink-0"></i>
            <span>故障排除时传递 <code className="px-2 py-1 bg-slate-800 rounded text-cyan-400">--verbose</code> 以将调试日志（握手、请求/响应、事件）从日志文件镜像到 stdio。</span>
          </li>
          <li className="flex items-start gap-2">
            <i className="ri-arrow-right-s-line text-green-400 mt-1 flex-shrink-0"></i>
            <span><code className="px-2 py-1 bg-slate-800 rounded text-cyan-400">--force</code> 使用 <code className="px-2 py-1 bg-slate-800 rounded text-cyan-400">lsof</code> 查找所选端口上的监听器，发送 SIGTERM，记录它终止了什么，然后启动 Gateway 网关（如果缺少 <code className="px-2 py-1 bg-slate-800 rounded text-cyan-400">lsof</code> 则快速失败）。</span>
          </li>
          <li className="flex items-start gap-2">
            <i className="ri-arrow-right-s-line text-green-400 mt-1 flex-shrink-0"></i>
            <span>如果你在 supervisor（launchd/systemd/mac 应用子进程模式）下运行，stop/restart 通常发送 <strong>SIGTERM</strong>；旧版本可能将其显示为 <code className="px-2 py-1 bg-slate-800 rounded text-cyan-400">pnpm</code> <code className="px-2 py-1 bg-slate-800 rounded text-cyan-400">ELIFECYCLE</code> 退出码 <strong>143</strong>（SIGTERM），这是正常关闭，不是崩溃。</span>
          </li>
          <li className="flex items-start gap-2">
            <i className="ri-arrow-right-s-line text-green-400 mt-1 flex-shrink-0"></i>
            <span><strong>SIGUSR1</strong> 在授权时触发进程内重启（Gateway 网关工具/配置应用/更新，或启用 <code className="px-2 py-1 bg-slate-800 rounded text-cyan-400">commands.restart</code> 以进行手动重启）。</span>
          </li>
          <li className="flex items-start gap-2">
            <i className="ri-arrow-right-s-line text-green-400 mt-1 flex-shrink-0"></i>
            <span>默认需要 Gateway 网关认证：设置 <code className="px-2 py-1 bg-slate-800 rounded text-cyan-400">gateway.auth.token</code>（或 <code className="px-2 py-1 bg-slate-800 rounded text-cyan-400">OPENCLAW_GATEWAY_TOKEN</code>）或 <code className="px-2 py-1 bg-slate-800 rounded text-cyan-400">gateway.auth.password</code>。客户端必须发送 <code className="px-2 py-1 bg-slate-800 rounded text-cyan-400">connect.params.auth.token/password</code>，除非使用 Tailscale Serve 身份。</span>
          </li>
          <li className="flex items-start gap-2">
            <i className="ri-arrow-right-s-line text-green-400 mt-1 flex-shrink-0"></i>
            <span>向导现在默认生成令牌，即使在 loopback 上也是如此。</span>
          </li>
          <li className="flex items-start gap-2">
            <i className="ri-arrow-right-s-line text-green-400 mt-1 flex-shrink-0"></i>
            <span>端口优先级：<code className="px-2 py-1 bg-slate-800 rounded text-cyan-400">--port</code> &gt; <code className="px-2 py-1 bg-slate-800 rounded text-cyan-400">OPENCLAW_GATEWAY_PORT</code> &gt; <code className="px-2 py-1 bg-slate-800 rounded text-cyan-400">gateway.port</code> &gt; 默认 <code className="px-2 py-1 bg-slate-800 rounded text-cyan-400">18789</code>。</span>
          </li>
        </ul>
      </div>

      <SectionTitle id="remote-access">远程访问</SectionTitle>
      <ul className="space-y-3 text-slate-300 text-base">
        <li className="flex items-start gap-2">
          <i className="ri-arrow-right-s-line text-green-400 mt-1 flex-shrink-0"></i>
          <span>首选 Tailscale/VPN；否则使用 SSH 隧道：</span>
        </li>
      </ul>
      <CodeBlock
        language="bash"
        code={`ssh -N -L 18789:127.0.0.1:18789 user@host`}
      />
      <ul className="space-y-3 text-slate-300 text-base mt-4">
        <li className="flex items-start gap-2">
          <i className="ri-arrow-right-s-line text-green-400 mt-1 flex-shrink-0"></i>
          <span>然后客户端通过隧道连接到 <code className="px-2 py-1 bg-slate-800 rounded text-cyan-400">ws://127.0.0.1:18789</code>。</span>
        </li>
        <li className="flex items-start gap-2">
          <i className="ri-arrow-right-s-line text-green-400 mt-1 flex-shrink-0"></i>
          <span>如果配置了令牌，即使通过隧道，客户端也必须在 <code className="px-2 py-1 bg-slate-800 rounded text-cyan-400">connect.params.auth.token</code> 中包含它。</span>
        </li>
      </ul>

      <SectionTitle id="multiple-gateways">多个 Gateway 网关（同一主机）</SectionTitle>
      <p className="text-slate-300 text-base leading-relaxed">
        通常不需要：一个 Gateway 网关可以服务多个消息渠道和智能体。仅在需要冗余或严格隔离（例如：救援机器人）时使用多个 Gateway 网关。
      </p>
      <p className="text-slate-300 text-base leading-relaxed mt-4">
        如果你隔离状态 + 配置并使用唯一端口，则支持。完整指南：多个 Gateway 网关。
      </p>

      <SubsectionTitle id="service-names">服务名称</SubsectionTitle>
      <p className="text-slate-300 text-base leading-relaxed mb-3">服务名称是配置文件感知的：</p>
      <ul className="space-y-3 text-slate-300 text-base">
        <li className="flex items-start gap-2">
          <i className="ri-arrow-right-s-line text-green-400 mt-1 flex-shrink-0"></i>
          <span>macOS：<code className="px-2 py-1 bg-slate-800 rounded text-cyan-400">bot.molt.&lt;profile&gt;</code>（旧版 <code className="px-2 py-1 bg-slate-800 rounded text-cyan-400">com.openclaw.*</code> 可能仍然存在）</span>
        </li>
        <li className="flex items-start gap-2">
          <i className="ri-arrow-right-s-line text-green-400 mt-1 flex-shrink-0"></i>
          <span>Linux：<code className="px-2 py-1 bg-slate-800 rounded text-cyan-400">openclaw-gateway-&lt;profile&gt;.service</code></span>
        </li>
        <li className="flex items-start gap-2">
          <i className="ri-arrow-right-s-line text-green-400 mt-1 flex-shrink-0"></i>
          <span>Windows：<code className="px-2 py-1 bg-slate-800 rounded text-cyan-400">OpenClaw Gateway (&lt;profile&gt;)</code></span>
        </li>
      </ul>

      <SubsectionTitle id="metadata">安装元数据</SubsectionTitle>
      <p className="text-slate-300 text-base leading-relaxed mb-3">安装元数据嵌入在服务配置中：</p>
      <ul className="space-y-3 text-slate-300 text-base">
        <li className="flex items-start gap-2">
          <i className="ri-arrow-right-s-line text-green-400 mt-1 flex-shrink-0"></i>
          <span><code className="px-2 py-1 bg-slate-800 rounded text-cyan-400">OPENCLAW_SERVICE_MARKER=openclaw</code></span>
        </li>
        <li className="flex items-start gap-2">
          <i className="ri-arrow-right-s-line text-green-400 mt-1 flex-shrink-0"></i>
          <span><code className="px-2 py-1 bg-slate-800 rounded text-cyan-400">OPENCLAW_SERVICE_KIND=gateway</code></span>
        </li>
        <li className="flex items-start gap-2">
          <i className="ri-arrow-right-s-line text-green-400 mt-1 flex-shrink-0"></i>
          <span><code className="px-2 py-1 bg-slate-800 rounded text-cyan-400">OPENCLAW_SERVICE_VERSION=&lt;version&gt;</code></span>
        </li>
      </ul>

      <p className="text-slate-300 text-base leading-relaxed mt-4">
        救援机器人模式：保持第二个 Gateway 网关隔离，使用自己的配置文件、状态目录、工作区和基础端口间隔。完整指南：救援机器人指南。
      </p>

      <SubsectionTitle id="dev-profile">Dev 配置文件（<code className="px-2 py-1 bg-slate-800 rounded text-cyan-400">--dev</code>）</SubsectionTitle>
      <p className="text-slate-300 text-base leading-relaxed mb-4">
        快速路径：运行完全隔离的 dev 实例（配置/状态/工作区）而不触及你的主设置。
      </p>
      <CodeBlock
        language="bash"
        code={`openclaw --dev setup
openclaw --dev gateway --allow-unconfigured
# 然后定位到 dev 实例：
openclaw --dev status
openclaw --dev health`}
      />

      <p className="text-slate-300 text-base leading-relaxed mt-6 mb-3">默认值（可通过 env/flags/config 覆盖）：</p>
      <ul className="space-y-3 text-slate-300 text-base">
        <li className="flex items-start gap-2">
          <i className="ri-arrow-right-s-line text-green-400 mt-1 flex-shrink-0"></i>
          <span><code className="px-2 py-1 bg-slate-800 rounded text-cyan-400">OPENCLAW_STATE_DIR=~/.openclaw-dev</code></span>
        </li>
        <li className="flex items-start gap-2">
          <i className="ri-arrow-right-s-line text-green-400 mt-1 flex-shrink-0"></i>
          <span><code className="px-2 py-1 bg-slate-800 rounded text-cyan-400">OPENCLAW_CONFIG_PATH=~/.openclaw-dev/openclaw.json</code></span>
        </li>
        <li className="flex items-start gap-2">
          <i className="ri-arrow-right-s-line text-green-400 mt-1 flex-shrink-0"></i>
          <span><code className="px-2 py-1 bg-slate-800 rounded text-cyan-400">OPENCLAW_GATEWAY_PORT=19001</code>（Gateway 网关 WS + HTTP）</span>
        </li>
        <li className="flex items-start gap-2">
          <i className="ri-arrow-right-s-line text-green-400 mt-1 flex-shrink-0"></i>
          <span>浏览器控制服务端口 = <code className="px-2 py-1 bg-slate-800 rounded text-cyan-400">19003</code>（派生：<code className="px-2 py-1 bg-slate-800 rounded text-cyan-400">gateway.port+2</code>，仅 loopback）</span>
        </li>
        <li className="flex items-start gap-2">
          <i className="ri-arrow-right-s-line text-green-400 mt-1 flex-shrink-0"></i>
          <span><code className="px-2 py-1 bg-slate-800 rounded text-cyan-400">canvasHost.port=19005</code>（派生：<code className="px-2 py-1 bg-slate-800 rounded text-cyan-400">gateway.port+4</code>）</span>
        </li>
        <li className="flex items-start gap-2">
          <i className="ri-arrow-right-s-line text-green-400 mt-1 flex-shrink-0"></i>
          <span>当你在 <code className="px-2 py-1 bg-slate-800 rounded text-cyan-400">--dev</code> 下运行 <code className="px-2 py-1 bg-slate-800 rounded text-cyan-400">setup</code>/<code className="px-2 py-1 bg-slate-800 rounded text-cyan-400">onboard</code> 时，<code className="px-2 py-1 bg-slate-800 rounded text-cyan-400">agents.defaults.workspace</code> 默认变为 <code className="px-2 py-1 bg-slate-800 rounded text-cyan-400">~/.openclaw/workspace-dev</code>。</span>
        </li>
      </ul>

      <SubsectionTitle id="derived-ports">派生端口（经验法则）</SubsectionTitle>
      <ul className="space-y-3 text-slate-300 text-base">
        <li className="flex items-start gap-2">
          <i className="ri-arrow-right-s-line text-green-400 mt-1 flex-shrink-0"></i>
          <span>基础端口 = <code className="px-2 py-1 bg-slate-800 rounded text-cyan-400">gateway.port</code>（或 <code className="px-2 py-1 bg-slate-800 rounded text-cyan-400">OPENCLAW_GATEWAY_PORT</code> / <code className="px-2 py-1 bg-slate-800 rounded text-cyan-400">--port</code>）</span>
        </li>
        <li className="flex items-start gap-2">
          <i className="ri-arrow-right-s-line text-green-400 mt-1 flex-shrink-0"></i>
          <span>浏览器控制服务端口 = 基础 + 2（仅 loopback）</span>
        </li>
        <li className="flex items-start gap-2">
          <i className="ri-arrow-right-s-line text-green-400 mt-1 flex-shrink-0"></i>
          <span><code className="px-2 py-1 bg-slate-800 rounded text-cyan-400">canvasHost.port = 基础 + 4</code>（或 <code className="px-2 py-1 bg-slate-800 rounded text-cyan-400">OPENCLAW_CANVAS_HOST_PORT</code> / 配置覆盖）</span>
        </li>
        <li className="flex items-start gap-2">
          <i className="ri-arrow-right-s-line text-green-400 mt-1 flex-shrink-0"></i>
          <span>浏览器配置文件 CDP 端口从 <code className="px-2 py-1 bg-slate-800 rounded text-cyan-400">browser.controlPort + 9 .. + 108</code> 自动分配（按配置文件持久化）。</span>
        </li>
      </ul>

      <SubsectionTitle id="checklist">每个实例的检查清单</SubsectionTitle>
      <ul className="space-y-3 text-slate-300 text-base">
        <li className="flex items-start gap-2">
          <i className="ri-checkbox-circle-line text-green-400 mt-1 flex-shrink-0"></i>
          <span>唯一的 <code className="px-2 py-1 bg-slate-800 rounded text-cyan-400">gateway.port</code></span>
        </li>
        <li className="flex items-start gap-2">
          <i className="ri-checkbox-circle-line text-green-400 mt-1 flex-shrink-0"></i>
          <span>唯一的 <code className="px-2 py-1 bg-slate-800 rounded text-cyan-400">OPENCLAW_CONFIG_PATH</code></span>
        </li>
        <li className="flex items-start gap-2">
          <i className="ri-checkbox-circle-line text-green-400 mt-1 flex-shrink-0"></i>
          <span>唯一的 <code className="px-2 py-1 bg-slate-800 rounded text-cyan-400">OPENCLAW_STATE_DIR</code></span>
        </li>
        <li className="flex items-start gap-2">
          <i className="ri-checkbox-circle-line text-green-400 mt-1 flex-shrink-0"></i>
          <span>唯一的 <code className="px-2 py-1 bg-slate-800 rounded text-cyan-400">agents.defaults.workspace</code></span>
        </li>
        <li className="flex items-start gap-2">
          <i className="ri-checkbox-circle-line text-green-400 mt-1 flex-shrink-0"></i>
          <span>单独的 WhatsApp 号码（如果使用 WA）</span>
        </li>
      </ul>

      <p className="text-slate-300 text-base leading-relaxed mt-6 mb-3">按配置文件安装服务：</p>
      <CodeBlock
        language="bash"
        code={`openclaw --profile main gateway install
openclaw --profile rescue gateway install`}
      />

      <p className="text-slate-300 text-base leading-relaxed mt-6 mb-3">示例：</p>
      <CodeBlock
        language="bash"
        code={`OPENCLAW_CONFIG_PATH=~/.openclaw/a.json OPENCLAW_STATE_DIR=~/.openclaw-a openclaw gateway --port 19001
OPENCLAW_CONFIG_PATH=~/.openclaw/b.json OPENCLAW_STATE_DIR=~/.openclaw-b openclaw gateway --port 19002`}
      />

      <SectionTitle id="protocol">协议（运维视角）</SectionTitle>
      <ul className="space-y-3 text-slate-300 text-base">
        <li className="flex items-start gap-2">
          <i className="ri-arrow-right-s-line text-green-400 mt-1 flex-shrink-0"></i>
          <span>完整文档：Gateway 网关协议 和 Bridge 协议（旧版）。</span>
        </li>
        <li className="flex items-start gap-2">
          <i className="ri-arrow-right-s-line text-green-400 mt-1 flex-shrink-0"></i>
          <span>客户端必须发送的第一帧：<code className="px-2 py-1 bg-slate-800 rounded text-cyan-400 text-xs">req &#123;type:"req", id, method:"connect", params:&#123;minProtocol,maxProtocol,client:&#123;id,displayName?,version,platform,deviceFamily?,modelIdentifier?,mode,instanceId?&#125;, caps, auth?, locale?, userAgent? &#125; &#125;</code>。</span>
        </li>
        <li className="flex items-start gap-2">
          <i className="ri-arrow-right-s-line text-green-400 mt-1 flex-shrink-0"></i>
          <span>Gateway 网关回复 <code className="px-2 py-1 bg-slate-800 rounded text-cyan-400">res &#123;type:"res", id, ok:true, payload:hello-ok &#125;</code>（或 <code className="px-2 py-1 bg-slate-800 rounded text-cyan-400">ok:false</code> 带错误，然后关闭）。</span>
        </li>
        <li className="flex items-start gap-2">
          <i className="ri-arrow-right-s-line text-green-400 mt-1 flex-shrink-0"></i>
          <div>
            <span>握手后：</span>
            <ul className="ml-4 mt-2 space-y-1 text-sm text-slate-400">
              <li>请求：<code className="px-2 py-1 bg-slate-800 rounded text-cyan-400">&#123;type:"req", id, method, params&#125;</code> → <code className="px-2 py-1 bg-slate-800 rounded text-cyan-400">&#123;type:"res", id, ok, payload|error&#125;</code></li>
              <li>事件：<code className="px-2 py-1 bg-slate-800 rounded text-cyan-400">&#123;type:"event", event, payload, seq?, stateVersion?&#125;</code></li>
            </ul>
          </div>
        </li>
        <li className="flex items-start gap-2">
          <i className="ri-arrow-right-s-line text-green-400 mt-1 flex-shrink-0"></i>
          <span>结构化 presence 条目：<code className="px-2 py-1 bg-slate-800 rounded text-cyan-400 text-xs">&#123;host, ip, version, platform?, deviceFamily?, modelIdentifier?, mode, lastInputSeconds?, ts, reason?, tags?[], instanceId? &#125;</code>（对于 WS 客户端，<code className="px-2 py-1 bg-slate-800 rounded text-cyan-400">instanceId</code> 来自 <code className="px-2 py-1 bg-slate-800 rounded text-cyan-400">connect.client.instanceId</code>）。</span>
        </li>
        <li className="flex items-start gap-2">
          <i className="ri-arrow-right-s-line text-green-400 mt-1 flex-shrink-0"></i>
          <span><code className="px-2 py-1 bg-slate-800 rounded text-cyan-400">agent</code> 响应是两阶段的：首先 <code className="px-2 py-1 bg-slate-800 rounded text-cyan-400">res</code> 确认 <code className="px-2 py-1 bg-slate-800 rounded text-cyan-400">&#123;runId,status:"accepted"&#125;</code>，然后在运行完成后发送最终 <code className="px-2 py-1 bg-slate-800 rounded text-cyan-400">res</code> <code className="px-2 py-1 bg-slate-800 rounded text-cyan-400">&#123;runId,status:"ok"|"error",summary&#125;</code>；流式输出作为 <code className="px-2 py-1 bg-slate-800 rounded text-cyan-400">event:"agent"</code> 到达。</span>
        </li>
      </ul>

      <SectionTitle id="methods">方法（初始集）</SectionTitle>
      <div className="grid md:grid-cols-2 gap-4">
        {[
          { method: 'health', desc: '完整健康快照（与 openclaw health --json 形状相同）' },
          { method: 'status', desc: '简短摘要' },
          { method: 'system-presence', desc: '当前 presence 列表' },
          { method: 'system-event', desc: '发布 presence/系统注释（结构化）' },
          { method: 'send', desc: '通过活跃渠道发送消息' },
          { method: 'agent', desc: '运行智能体轮次（在同一连接上流回事件）' },
          { method: 'node.list', desc: '列出已配对 + 当前连接的节点' },
          { method: 'node.describe', desc: '描述节点（能力 + 支持的命令）' },
          { method: 'node.invoke', desc: '在节点上调用命令（例如 canvas.*、camera.*）' },
          { method: 'node.pair.*', desc: '配对生命周期（request、list、approve、reject、verify）' }
        ].map((item, idx) => (
          <div key={idx} className="bg-slate-900/50 border border-slate-800/50 rounded-lg p-4">
            <code className="text-green-400 font-semibold text-base">{item.method}</code>
            <p className="text-base text-slate-400 mt-1">{item.desc}</p>
          </div>
        ))}
      </div>

      <p className="text-slate-300 text-base leading-relaxed mt-6">
        另见：Presence 了解 presence 如何产生/去重以及为什么稳定的 <code className="px-2 py-1 bg-slate-800 rounded text-cyan-400">client.instanceId</code> 很重要。
      </p>

      <SectionTitle id="events">事件</SectionTitle>
      <ul className="space-y-3 text-slate-300 text-base">
        <li className="flex items-start gap-2">
          <i className="ri-arrow-right-s-line text-green-400 mt-1 flex-shrink-0"></i>
          <span><code className="px-2 py-1 bg-slate-800 rounded text-cyan-400">agent</code> — 来自智能体运行的流式工具/输出事件（带 seq 标记）。</span>
        </li>
        <li className="flex items-start gap-2">
          <i className="ri-arrow-right-s-line text-green-400 mt-1 flex-shrink-0"></i>
          <span><code className="px-2 py-1 bg-slate-800 rounded text-cyan-400">presence</code> — presence 更新（带 stateVersion 的增量）推送到所有连接的客户端。</span>
        </li>
        <li className="flex items-start gap-2">
          <i className="ri-arrow-right-s-line text-green-400 mt-1 flex-shrink-0"></i>
          <span><code className="px-2 py-1 bg-slate-800 rounded text-cyan-400">tick</code> — 定期保活/无操作以确认活跃。</span>
        </li>
        <li className="flex items-start gap-2">
          <i className="ri-arrow-right-s-line text-green-400 mt-1 flex-shrink-0"></i>
          <span><code className="px-2 py-1 bg-slate-800 rounded text-cyan-400">shutdown</code> — Gateway 网关正在退出；payload 包括 <code className="px-2 py-1 bg-slate-800 rounded text-cyan-400">reason</code> 和可选的 <code className="px-2 py-1 bg-slate-800 rounded text-cyan-400">restartExpectedMs</code>。客户端应重新连接。</span>
        </li>
      </ul>

      <SectionTitle id="webchat">WebChat 集成</SectionTitle>
      <ul className="space-y-3 text-slate-300 text-base">
        <li className="flex items-start gap-2">
          <i className="ri-arrow-right-s-line text-green-400 mt-1 flex-shrink-0"></i>
          <span>WebChat 是原生 SwiftUI UI，直接与 Gateway 网关 WebSocket 通信以获取历史记录、发送、中止和事件。</span>
        </li>
        <li className="flex items-start gap-2">
          <i className="ri-arrow-right-s-line text-green-400 mt-1 flex-shrink-0"></i>
          <span>远程使用通过相同的 SSH/Tailscale 隧道；如果配置了 Gateway 网关令牌，客户端在 <code className="px-2 py-1 bg-slate-800 rounded text-cyan-400">connect</code> 期间包含它。</span>
        </li>
        <li className="flex items-start gap-2">
          <i className="ri-arrow-right-s-line text-green-400 mt-1 flex-shrink-0"></i>
          <span>macOS 应用通过单个 WS 连接（共享连接）；它从初始快照填充 presence 并监听 <code className="px-2 py-1 bg-slate-800 rounded text-cyan-400">presence</code> 事件以更新 UI。</span>
        </li>
      </ul>

      <SectionTitle id="cli-management">Gateway 网关服务管理（CLI）</SectionTitle>
      <p className="text-slate-300 text-base leading-relaxed mb-4">
        使用 Gateway 网关 CLI 进行 install/start/stop/restart/status：
      </p>
      <CodeBlock
        language="bash"
        code={`openclaw gateway status
openclaw gateway install
openclaw gateway stop
openclaw gateway restart
openclaw logs --follow`}
      />

      <InfoBox type="info" title="注意事项">
        <ul className="space-y-2 text-sm">
          <li><code className="px-2 py-1 bg-slate-800 rounded text-cyan-400">gateway status</code> 默认使用服务解析的端口/配置探测 Gateway 网关 RPC（使用 <code className="px-2 py-1 bg-slate-800 rounded text-cyan-400">--url</code> 覆盖）。</li>
          <li><code className="px-2 py-1 bg-slate-800 rounded text-cyan-400">gateway status --deep</code> 添加系统级扫描（LaunchDaemons/系统单元）。</li>
          <li><code className="px-2 py-1 bg-slate-800 rounded text-cyan-400">gateway status --no-probe</code> 跳过 RPC 探测（在网络故障时有用）。</li>
          <li><code className="px-2 py-1 bg-slate-800 rounded text-cyan-400">gateway status --json</code> 对脚本是稳定的。</li>
        </ul>
      </InfoBox>

      <SectionTitle id="next-steps">下一步</SectionTitle>
      <div className="grid md:grid-cols-2 gap-4">
        {[
          { icon: 'ri-settings-3-line', title: '配置', desc: '了解如何配置网关的各项参数' },
          { icon: 'ri-shield-keyhole-line', title: '认证', desc: '配置认证方式和权限控制' },
          { icon: 'ri-lock-line', title: '安全', desc: '学习网关的安全最佳实践' },
          { icon: 'ri-file-list-3-line', title: '日志', desc: '配置和查看网关日志' }
        ].map((item, idx) => (
          <div
            key={idx}
            className="flex items-start gap-3 bg-slate-900/50 border border-slate-800/50 rounded-xl p-4 hover:border-green-500/30 transition-all group cursor-pointer"
          >
            <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-green-500/30 transition-colors">
              <i className={`${item.icon} text-xl text-green-400`}></i>
            </div>
            <div>
              <h3 className="font-semibold text-white mb-1 group-hover:text-green-400 transition-colors">{item.title}</h3>
              <p className="text-sm text-slate-400">{item.desc}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default GatewayOverviewContent;